import {customThemeConstants} from '@hconnect/uikit/src/lib2'
import {Box, useTheme, Stack} from '@mui/material'
import * as d3 from 'd3'
import moment from 'moment-timezone'
import {useCallback, useMemo, useRef} from 'react'
import {v4 as uuidV4} from 'uuid'

import {
  ChartLine,
  ChartPath,
  DatetimeValueChartTooltip
} from '../../../../../shared/components/d3chart'
import {StockDevelopmentTooltipContent} from '../../../../../shared/components/stockDevelopmentChart/StockDevelopmentTooltipContent'
import {useDateScale, useLinearScale} from '../../../../../shared/helpers/scale'
import {useTargetLevelsDaily} from '../../../../../shared/hooks/minTargetLevelsDaily'
import {usePlantConfig} from '../../../../../shared/hooks/usePlantConfigData'
import {DatetimeValue} from '../../../../../shared/interfaces/common'
import {StockDevelopmentData} from '../../../../../shared/selectors/stockDevelopment'
import {getListOfDays} from '../../../../../shared/selectors/time'
import {usePlanningChartStartEnd} from '../../../../page-planning/dailyPlanning/PlanningChartStartEndProvider'
import {useStockDevelopmentChartMinMaxRanges} from '../../utils/useOptimizerStockChartMinMaxRange'

import {OptimizerStockLevelsChartLabels} from './OptimizerStockLevelsChartLabels'

type OptimizerStockLevelsChartProps = {
  storageCapacity: number
  minTargetLevel: number
  minTargetLevelWeekend: number
  stockDevelopmentData: StockDevelopmentData
  stockDevelopmentSecondaryData?: StockDevelopmentData
  width: number
  height: number
}

export const OptimizerStockLevelsChartContent = ({
  storageCapacity,
  minTargetLevel,
  minTargetLevelWeekend,
  stockDevelopmentData,
  stockDevelopmentSecondaryData,
  width,
  height
}: OptimizerStockLevelsChartProps) => {
  const {palette} = useTheme()

  const chartRef = useRef<HTMLDivElement | SVGSVGElement>(null)

  const {timezone_id: timezoneId} = usePlantConfig()

  const {startOfChart, endOfChart} = usePlanningChartStartEnd()

  const {minDomainValue, maxDomainValue} = useStockDevelopmentChartMinMaxRanges({
    stockData: {...stockDevelopmentData, stockOverwrites: undefined},
    storageCapacity,
    minTargetLevels: {
      week: minTargetLevel,
      weekend: minTargetLevelWeekend
    }
  })

  const listOfDays = getListOfDays({
    start: startOfChart.clone().startOf('day'),
    end: endOfChart.clone().add(1, 'day').endOf('day')
  })

  const minTargetLevelDatetimeValues = useTargetLevelsDaily({
    minTargetLevels: {weekend: minTargetLevelWeekend, week: minTargetLevel},
    listOfDays,
    timezoneId
  })

  const xScale = useDateScale({
    domain: [startOfChart, endOfChart],
    range: [0, width]
  })

  const minY = minDomainValue < 0 ? minDomainValue * 1.1 : minDomainValue * 0.9
  const maxY = maxDomainValue > 0 ? maxDomainValue * 1.1 : maxDomainValue * 0.9

  const yScale = useLinearScale({
    domain: [minY, maxY],
    range: [height, 0]
  })

  const chartTopClipPathFn = d3
    .line<DatetimeValue>()
    .x((d: DatetimeValue) => xScale(moment.utc(d.datetime)))
    .y((d: DatetimeValue) => yScale(d.value))

  const clipAbovePath = minTargetLevelDatetimeValues
    ? chartTopClipPathFn([
        {datetime: moment.utc(startOfChart).toISOString(), value: maxDomainValue},
        ...minTargetLevelDatetimeValues,
        {datetime: moment.utc(endOfChart).toISOString(), value: maxDomainValue}
      ]) ?? undefined
    : undefined

  const clipBelowPath = minTargetLevelDatetimeValues
    ? chartTopClipPathFn([
        {datetime: moment.utc(startOfChart).toISOString(), value: minDomainValue},
        ...minTargetLevelDatetimeValues,
        {datetime: moment.utc(endOfChart).toISOString(), value: minDomainValue}
      ]) ?? undefined
    : undefined

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const clipAboveId = useMemo(() => `clip-above-min-level-${uuidV4()}`, [clipAbovePath])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const clipBelowId = useMemo(() => `clip-below-min-level-${uuidV4()}`, [clipAbovePath])

  const {
    stockLevel: {combined: combinedStockLevel}
  } = stockDevelopmentData

  const combinedStockLevelSecondary: DatetimeValue<string, number>[] | undefined =
    stockDevelopmentSecondaryData?.stockLevel.combined

  const formatTooltipContent = useCallback(
    (datetimeValue: DatetimeValue) => (
      <StockDevelopmentTooltipContent
        canCreateStockOverwrite={false}
        datetimeValue={datetimeValue}
        stockOverwrites={[]}
      />
    ),
    []
  )

  return (
    <Stack direction="row">
      <OptimizerStockLevelsChartLabels storageCapacity={storageCapacity} yScale={yScale} />
      <Box ref={chartRef} style={{position: 'relative'}}>
        <svg height={height} width={width} viewBox={`0 0 ${width} ${height}`}>
          <defs>
            <clipPath id={clipAboveId} clipPathUnits="userSpaceOnUse">
              <path d={clipAbovePath + 'Z'} />
            </clipPath>
            <clipPath id={clipBelowId} clipPathUnits="userSpaceOnUse">
              <path d={clipBelowPath + 'Z'} />
            </clipPath>
          </defs>

          <ChartLine
            variant="horizontal"
            xScale={xScale}
            yScale={yScale}
            xDomain={[listOfDays[0], listOfDays[listOfDays.length - 1]]}
            yValue={storageCapacity}
            overrideLineAttributes={{
              stroke: palette.grey[500]
            }}
          />

          <ChartLine
            variant="horizontal"
            xScale={xScale}
            yScale={yScale}
            xDomain={[listOfDays[0], listOfDays[listOfDays.length - 1]]}
            yValue={0}
            overrideLineAttributes={{
              stroke: palette.grey[500]
            }}
          />

          <g clipPath={`url(#${clipAboveId})`}>
            <ChartPath datetimeValues={combinedStockLevel} xScale={xScale} yScale={yScale} fill />
          </g>
          <g clipPath={`url(#${clipBelowId})`}>
            <ChartPath
              datetimeValues={combinedStockLevel}
              xScale={xScale}
              yScale={yScale}
              overridePathAttributes={{
                stroke: palette.error.light,
                shapeRendering: 'geometricPrecision'
              }}
              fill
              gradient="bottom"
            />
          </g>
          {combinedStockLevelSecondary && (
            <ChartPath
              overridePathAttributes={{stroke: customThemeConstants().palette.accent1}}
              datetimeValues={combinedStockLevelSecondary}
              xScale={xScale}
              yScale={yScale}
              fill
            />
          )}
          {minTargetLevelDatetimeValues && (
            <ChartPath
              datetimeValues={minTargetLevelDatetimeValues}
              xScale={xScale}
              yScale={yScale}
              overridePathAttributes={{
                stroke: palette.error.light,
                shapeRendering: 'geometricPrecision',
                strokeDasharray: '8,4',
                strokeWidth: '1px'
              }}
              testId="min_target_level_line"
            />
          )}
        </svg>
        <DatetimeValueChartTooltip
          chartRef={chartRef}
          xScale={xScale}
          yScale={yScale}
          datetimeValues={stockDevelopmentData.stockLevel.combined}
          formatTooltip={formatTooltipContent}
          variant="light"
        />
      </Box>
    </Stack>
  )
}
